import { Inject, Injectable } from "@angular/core";
import type { IIdentityProfilDataInfo } from "@kiraah-shared/common";
import { firstValueFrom } from "rxjs";
import { MOBILE_AUTH_SERVICE, IMobileAuthService } from "../../auth/mobile-auth.service.interface";
import { MobileAppCacheService } from "../../cache/mobile-app-cache.service";
import { MobileAuthResult } from "../../models/mobile-auth.models";
import { IdentityProfilData, TenantAppMobileSummary } from "../../models/mobile-profile.models";
import { MobileAuthStore } from "../../state/mobile-auth.store";
import { MobileProfileStore } from "../../state/mobile-profile.store";
import { MobileEntityProfilApiClient } from "./mobile-entity-profil-api.client";
import { mergeProfileApplicationsFromBackend } from "../helpers/mobile-tenant-applications";

export interface MobileAppsQueryOptions {
  forceRefresh?: boolean;
}

@Injectable({ providedIn: "root" })
export class MobileProfileService {
  constructor(
    @Inject(MOBILE_AUTH_SERVICE) private readonly authService: IMobileAuthService,
    private readonly authStore: MobileAuthStore,
    private readonly profileStore: MobileProfileStore,
    private readonly cacheService: MobileAppCacheService,
    private readonly entityProfilApiClient: MobileEntityProfilApiClient
  ) {}

  hydrateFromAuthState(): void {
    const authResult = this.authStore.authResult();
    const profile = authResult?.ProfilData as IdentityProfilData | undefined;
    if (!profile) {
      this.profileStore.clear();
      return;
    }

    this.applyProfilData(profile);
    void this.restorePersistedActiveApp(profile);
  }

  async tryRestoreFromCache(): Promise<boolean> {
    const profile = await this.cacheService.readProfile();
    if (!profile) {
      return false;
    }

    this.applyProfilData(profile);
    await this.restorePersistedActiveApp(profile);
    return true;
  }

  /** GET /me — aligné web `getCurrentProfile` → `ContextService.setContextData`. */
  async refreshFromBackend(): Promise<void> {
    const result = await this.authService.me();
    if (!result?.ProfilData) {
      return;
    }

    const existing = this.profileStore.profile();
    const loaded = result.ProfilData as IdentityProfilData;
    const profile = existing
      ? {
          ...loaded,
          Identity: loaded.Identity,
          Applications: mergeProfileApplicationsFromBackend(existing.Applications, loaded.Applications),
        }
      : loaded;
    const authResult: MobileAuthResult = {
      ...result,
      ProfilData: profile as MobileAuthResult["ProfilData"],
    };

    this.authStore.setSession(authResult);
    this.applyProfilData(profile);
    await this.restorePersistedActiveApp(profile);
    await this.cacheService.saveAuthResult(authResult).catch(() => undefined);
  }

  /**
   * Aligné web `app-initializer` : uniquement GET /me au F5 (droits + Applications).
   */
  async refreshSessionFromBackend(): Promise<void> {
    try {
      await this.refreshFromBackend();
    } catch {
      // Conserver le cache local.
    }
  }

  /** Launcher — aligné web header : liste `Applications` depuis `/me` uniquement. */
  async refreshLauncherAppsFromBackend(): Promise<void> {
    await this.refreshFromBackend();
  }

  /**
   * Recharge le contexte profil pour une application tenant (aligné web `EntityProfilService.switchActiveApp`).
   */
  async switchActiveApp(tenantAppUid: string): Promise<boolean> {
    const normalizedUid = tenantAppUid.trim();
    if (!normalizedUid) {
      return false;
    }

    if (this.profileStore.activeTenantAppUid() === normalizedUid) {
      await this.cacheService.saveActiveTenantAppUid(normalizedUid);
      return true;
    }

    try {
      const profilData = await firstValueFrom(
        this.entityProfilApiClient.loadIdentityProfilData(normalizedUid)
      );
      if (!profilData) {
        return false;
      }

      await this.applyProfilDataFromBackend(profilData, normalizedUid);
      return true;
    } catch {
      return false;
    }
  }

  /** Recharge uniquement les droits (`Identity.Profils`) sans écraser les menus. */
  async reloadActiveAppPermissions(tenantAppUid: string): Promise<boolean> {
    const normalizedUid = tenantAppUid.trim();
    if (!normalizedUid) {
      return false;
    }

    try {
      const profilData = await firstValueFrom(
        this.entityProfilApiClient.loadIdentityProfilData(normalizedUid)
      );
      if (!profilData?.Identity) {
        return false;
      }

      await this.applyPermissionsFromBackend(profilData);
      return true;
    } catch {
      return false;
    }
  }

  private async restorePersistedActiveApp(profile: IdentityProfilData): Promise<void> {
    try {
      const persistedUid = await this.cacheService.readActiveTenantAppUid();
      if (!persistedUid) {
        return;
      }
      const isStillValid = profile.Applications?.some((app) => String(app.Uid) === persistedUid);
      if (!isStillValid) {
        await this.cacheService.saveActiveTenantAppUid(null);
        return;
      }
      if (this.profileStore.activeTenantAppUid() !== persistedUid) {
        this.profileStore.setActiveTenantAppUid(persistedUid);
      }
    } catch {
      // cache indisponible: on garde le comportement par defaut (ActiveApp serveur).
    }
  }

  async getMobileApps(options: MobileAppsQueryOptions = {}): Promise<TenantAppMobileSummary[]> {
    const forceRefresh = options.forceRefresh ?? false;

    if (!forceRefresh) {
      const appsFromStore = this.profileStore.availableApplications();
      if (appsFromStore.length > 0) {
        return appsFromStore;
      }

      const hasRestoredProfile = await this.tryRestoreFromCache();
      if (hasRestoredProfile) {
        const appsFromCachedProfile = this.profileStore.availableApplications();
        if (appsFromCachedProfile.length > 0) {
          await this.cacheService.saveAvailableApps(appsFromCachedProfile).catch(() => undefined);
          return appsFromCachedProfile;
        }
      }

      const cachedApps = await this.cacheService.readAvailableApps();
      if (cachedApps.length > 0) {
        return cachedApps;
      }
    }

    try {
      await this.refreshLauncherAppsFromBackend();
      const appsFromBackend = this.profileStore.availableApplications();
      if (appsFromBackend.length > 0) {
        await this.cacheService.saveAvailableApps(appsFromBackend).catch(() => undefined);
        return appsFromBackend;
      }
    } catch {
      const appsFromStore = this.profileStore.availableApplications();
      if (appsFromStore.length > 0) {
        return appsFromStore;
      }
    }

    return this.cacheService.readAvailableApps();
  }

  private applyProfilData(profile: IdentityProfilData): void {
    this.profileStore.setProfile(profile);
    this.cacheService.saveProfile(profile).catch(() => undefined);
    this.cacheService
      .saveAvailableApps(this.profileStore.availableApplications())
      .catch(() => undefined);
  }

  private async applyPermissionsFromBackend(profilData: IIdentityProfilDataInfo): Promise<void> {
    const existingProfile = this.profileStore.profile();
    const authResult = this.authStore.authResult();
    if (!existingProfile || !authResult?.ProfilData || !profilData.Identity) {
      return;
    }

    const mergedProfilData = {
      ...authResult.ProfilData,
      Identity: profilData.Identity,
    } as IIdentityProfilDataInfo;

    this.authStore.updateProfilData(mergedProfilData);

    const mergedProfile: IdentityProfilData = {
      ...existingProfile,
      Identity: profilData.Identity,
    };
    this.applyProfilData(mergedProfile);

    await this.cacheService
      .saveAuthResult({
        ...authResult,
        ProfilData: mergedProfilData as MobileAuthResult["ProfilData"],
      })
      .catch(() => undefined);
  }

  private async applyProfilDataFromBackend(
    profilData: IIdentityProfilDataInfo,
    activeTenantAppUid: string
  ): Promise<void> {
    const loaded = profilData as IdentityProfilData;
    const existing = this.profileStore.profile();
    const profile: IdentityProfilData = existing
      ? {
          ...loaded,
          Applications: mergeProfileApplicationsFromBackend(existing.Applications, loaded.Applications),
        }
      : loaded;

    this.authStore.updateProfilData(profile as IIdentityProfilDataInfo);
    this.profileStore.setProfile(profile);
    this.profileStore.setActiveTenantAppUid(activeTenantAppUid);
    await Promise.all([
      this.cacheService.saveProfile(profile),
      this.cacheService.saveAvailableApps(this.profileStore.availableApplications()),
      this.cacheService.saveActiveTenantAppUid(activeTenantAppUid),
    ]);
  }
}
